import { Image } from 'expo-image';
import { router, useLocalSearchParams } from 'expo-router';
import { FlatList, Platform, ScrollView, StyleSheet, useWindowDimensions, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useGetNonProfitOrgs } from '@/api/hooks/nonProfitOrgHooks';
import { TrendingItem } from '@/api/models/trendingModels';
import { bs_icon } from '@/assets/images';
import FavoriteButton from '@/components/buttons/FavoriteButton';
import DotsLoader from '@/components/DotsLoader';
import ListEmptyComponent from '@/components/ListEmptyComponent';
import NonProfitOrganizationCard from '@/components/npo/NonProfitOrganizationCard';
import Text from '@/components/Text';
import PlatformContainer from '@/components/web/PlatformContainer';
import { useColor } from '@/hooks/useColor';
import useOnLightRatingPress from '@/hooks/useOnLightRatingPress';
import { formatDayTime } from '@/utils/dateFormats';
import { firebaseImage, safeJsonParseArray, ThumbSize } from '@/utils/firebaseImage';
import { i18n } from '@/utils/i18n';

const TrendingDetail = () => {
  const { trendingJson } = useLocalSearchParams<{ trendingJson: string }>();
  const colors = useColor();
  const { width } = useWindowDimensions();
  const { onLightRatingPress } = useOnLightRatingPress();

  //Locales
  const empty_charities = i18n.t('trending_detail.empty_charities');
  const npos_in_area = i18n.t('trending_detail.npos_area');

  const parseTrending = (): TrendingItem | undefined => {
    if (!trendingJson) return undefined;
    try {
      return JSON.parse(trendingJson) as TrendingItem;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  };

  const trendingDetail = parseTrending();

  const { data: charitiesResponse } = useGetNonProfitOrgs({
    radio: 300,
    type: 'charities',
    n: 0,
    limit: 4,
    latitude: trendingDetail?.location?.coordinates?.[0],
    longitude: trendingDetail?.location?.coordinates?.[1]
  });

  const charitiesData = charitiesResponse?.pages.flatMap((page) => page.results);

  const suggestionsWidth = width * 0.5 - (18 + style.flatListColumnWrapperStyle.gap * 0.5);
  const webSuggestionsWidth =
    width >= 1152
      ? 1152 * 0.5 - style.flatListColumnWrapperStyle.gap * 0.5 - 80 //Platform horizontal Padding
      : width >= 640
        ? width * 0.5 - style.flatListColumnWrapperStyle.gap * 0.5 - 80 //Platform horizontal Padding
        : width * 0.5 - style.flatListColumnWrapperStyle.gap * 0.5 - 32; //Platform small device horizontal Padding

  const renderSuggestions = () => (
    <FlatList
      style={style.flatList}
      contentContainerStyle={style.flatListContentContainerStyle}
      columnWrapperStyle={style.flatListColumnWrapperStyle}
      keyExtractor={(item) => item.id}
      numColumns={2}
      scrollEnabled={false}
      data={charitiesData}
      showsVerticalScrollIndicator={false}
      renderItem={({ item }) => {
        return (
          <View
            style={{
              width: Platform.OS === 'web' ? webSuggestionsWidth : suggestionsWidth
            }}>
            <NonProfitOrganizationCard
              npoId={item.id}
              key={item.id}
              title={item.name}
              subtitle={item.description}
              assets={safeJsonParseArray(item.thumbnail) ?? '[]'}
              lightRatingValue={parseFloat(item.lightRaitingScore ?? '0')}
              claimed={item.claimed}
              onPress={() =>
                router.navigate({
                  pathname: `/npoDetail/[npoId]/insights`,
                  params: {
                    type: item.type,
                    npoId: item.id,
                    thumbnail: item.thumbnail,
                    name: item.name,
                    lightRating: parseFloat(item.lightRaitingScore ?? '0')
                  }
                })
              }
              onLightRatingPress={() =>
                onLightRatingPress({
                  name: item.name,
                  lightRating: parseFloat(item.lightRaitingScore ?? '0')
                })
              }
              size={Platform.OS === 'web' ? webSuggestionsWidth : suggestionsWidth}
              lightRatingVariant={width < 768 ? 'small' : 'medium'}
              lightRatingPositon={4}
              showFavoriteButton={false}
            />
          </View>
        );
      }}
      ListEmptyComponent={<ListEmptyComponent label={empty_charities} />}
    />
  );

  return (
    <PlatformContainer>
      <SafeAreaView style={style.container} edges={['bottom']}>
        <View style={style.container}>
          {trendingDetail === undefined ? (
            <DotsLoader />
          ) : (
            <ScrollView contentContainerStyle={style.scrollView}>
              {/* Trending Detail */}
              <View style={style.details}>
                <View style={Platform.OS === 'web' ? style.imageContainerWeb : style.imageContainer}>
                  <View style={style.favoriteIcon}>
                    <FavoriteButton itemId={trendingDetail?.id} type="trendingPost" />
                  </View>
                  <Image
                    source={
                      trendingDetail?.thumbnail
                        ? firebaseImage(safeJsonParseArray(trendingDetail.thumbnail ?? '[]')?.at(0), ThumbSize.Large)
                        : bs_icon
                    }
                    style={style.image}
                    contentFit="cover"
                  />
                </View>
                <Text fontWeight="500" style={style.title}>
                  {trendingDetail?.title}
                </Text>
                <Text style={[style.info, { color: colors.text }]}>{formatDayTime(trendingDetail?.eventDate)}</Text>
                <Text style={[style.info, { color: colors.text }]}>{trendingDetail?.description}</Text>
              </View>

              {/* Non profits in the area */}
              <Text fontWeight="500" style={style.title}>
                {npos_in_area}
              </Text>
              {renderSuggestions()}
            </ScrollView>
          )}
        </View>
      </SafeAreaView>
    </PlatformContainer>
  );
};

export default TrendingDetail;

const style = StyleSheet.create({
  container: {
    flex: 1
  },

  details: {
    marginBottom: 38,
    rowGap: 10
  },

  scrollView: {
    width: '100%',
    paddingHorizontal: Platform.OS !== 'web' ? 18 : 0
  },

  imageContainer: {
    width: '100%',
    aspectRatio: 1,
    marginBottom: 10
  },

  imageContainerWeb: {
    width: '50%',
    aspectRatio: 1,
    marginBottom: 10,
    marginHorizontal: 'auto'
  },

  image: {
    width: '100%',
    height: '100%',
    borderRadius: 10
  },

  title: {
    fontSize: 22,
    color: 'black'
  },

  info: {
    fontSize: 14,
    fontWeight: '400',
    letterSpacing: -0.28
  },

  flatList: {
    marginTop: 20
  },

  //Vertical Gap for flatList
  flatListContentContainerStyle: {
    gap: 25
  },

  //Horizontal Gap for flatList
  flatListColumnWrapperStyle: {
    gap: 22
  },

  favoriteIcon: {
    height: 28,
    width: 28,
    position: 'absolute',
    top: 14,
    right: 14,
    zIndex: 10
  }
});
